/**
 * Çerçeve bileşenlerinin gerçek API çağrıları (WP 2.8). Sahte veri yok; her çağrı sunucunun projekte yanıtını döner.
 * İmzalı dosya indirme adresleri burada DÖNDÜRÜLMEZ ve önbelleğe alınmaz (her indirmede yeni belirteç: `downloadFile`).
 */
import type {
  ActivityResponse,
  Attachment,
  BulkRecordActionInput,
  BulkRecordActionResult,
  CompanyContactsResponse,
  CreateCrmRecordInput,
  CreateLookupInput,
  CrmRecordType,
  Lookup,
  LookupKind,
  MergePreview,
  NotificationList,
  RecordStats,
  SimilarCheckInput,
  SimilarRecord,
  UpdateCrmRecordInput,
  UpdateLookupInput,
  CreateReminderInput,
  CreateSavedListInput,
  Department,
  DirectoryUsersResponse,
  FieldCandidatesResponse,
  InitiateUploadResult,
  ListCatalogResponse,
  ListsResponse,
  RecordAcl,
  RecordQueryInput,
  RecordQueryResponse,
  RecordView,
  RefLabels,
  Reminder,
  ResolveRefsInput,
  SavedList,
  SetRecordAclInput,
  SnoozeReminderInput,
  Tag,
  UpdateSavedListInput,
} from '@castinflow/shared';
import { errorEnvelopeSchema } from '@castinflow/shared';
import { ApiError, apiFetch } from '../lib/api';
import { apiMutate, csrfHeaders } from '../lib/session';

const enc = encodeURIComponent;

// --- Liste ---
export function queryRecords(
  type: string,
  input: Partial<RecordQueryInput>,
  signal?: AbortSignal,
): Promise<RecordQueryResponse> {
  return apiFetch<RecordQueryResponse>(`/records/${enc(type)}/query`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...csrfHeaders() },
    body: JSON.stringify(input),
    ...(signal ? { signal } : {}),
  });
}
export const fetchCatalog = (type: string) =>
  apiFetch<ListCatalogResponse>(`/records/${enc(type)}/list-catalog`);
export const fetchLists = (type: string) => apiFetch<ListsResponse>(`/records/${enc(type)}/lists`);
export const createList = (type: string, input: CreateSavedListInput) =>
  apiMutate<SavedList>(`/records/${enc(type)}/lists`, 'POST', input);
export const updateList = (type: string, id: string, input: UpdateSavedListInput) =>
  apiMutate<SavedList>(`/records/${enc(type)}/lists/${enc(id)}`, 'PATCH', input);
export function deleteList(type: string, id: string, version: number): Promise<null> {
  return apiFetch<null>(`/records/${enc(type)}/lists/${enc(id)}`, {
    method: 'DELETE',
    headers: { 'if-match': `"${version}"`, ...csrfHeaders() },
  });
}

// --- Kayıt ---
export async function fetchRecordView(type: string, id: string): Promise<RecordView> {
  const r = await apiFetch<{ view: RecordView }>(`/records/${enc(type)}/${enc(id)}`);
  return r.view;
}
export function patchCustomValues(
  type: string,
  id: string,
  patch: Record<string, unknown>,
  version: string | null,
): Promise<{ id: string; customValues: Record<string, unknown>; version: string }> {
  return apiFetch(`/records/${enc(type)}/${enc(id)}/custom-values`, {
    method: 'PATCH',
    headers: {
      'content-type': 'application/json',
      ...csrfHeaders(),
      ...(version === null ? {} : { 'if-match': `"${version}"` }),
    },
    body: JSON.stringify(patch),
  });
}
export const fetchAcl = (type: string, id: string) =>
  apiFetch<RecordAcl>(`/records/${enc(type)}/${enc(id)}/acl`);
export const putAcl = (type: string, id: string, input: SetRecordAclInput) =>
  apiMutate<RecordAcl>(`/records/${enc(type)}/${enc(id)}/acl`, 'PUT', input);
export const resolveRefs = (input: Partial<ResolveRefsInput>) =>
  apiMutate<RefLabels>('/refs/resolve', 'POST', input);

// --- Seçiciler ---
export function fetchCandidates(
  type: string,
  key: string,
  q: { q: string; targetType?: string | undefined; cursor?: string | undefined; limit?: number },
  signal?: AbortSignal,
): Promise<FieldCandidatesResponse> {
  const p = new URLSearchParams({ q: q.q, limit: String(q.limit ?? 10) });
  if (q.targetType) p.set('targetType', q.targetType);
  if (q.cursor) p.set('cursor', q.cursor);
  return apiFetch(`/records/${enc(type)}/fields/${enc(key)}/candidates?${p.toString()}`, {
    ...(signal ? { signal } : {}),
  });
}
export function fetchDirectoryUsers(
  q: { q: string; cursor?: string | undefined; limit?: number },
  signal?: AbortSignal,
): Promise<DirectoryUsersResponse> {
  const p = new URLSearchParams({ q: q.q, limit: String(q.limit ?? 20) });
  if (q.cursor) p.set('cursor', q.cursor);
  return apiFetch(`/directory/users?${p.toString()}`, { ...(signal ? { signal } : {}) });
}
export const fetchDepartments = async () =>
  (await apiFetch<{ departments: Department[] }>('/departments')).departments;

// --- WP 2.5 bağlantıları ---
export const fetchRecordTags = async (type: string, id: string) =>
  (await apiFetch<{ data: Tag[] }>(`/records/${enc(type)}/${enc(id)}/tags`)).data;
export const fetchTagOptions = async (q: string) =>
  (await apiFetch<{ data: Tag[] }>(`/tags?limit=100${q ? `&q=${enc(q)}` : ''}`)).data;
export const addRecordTag = (
  type: string,
  id: string,
  pick: { tagId: string } | { name: string },
) => apiMutate<unknown>(`/records/${enc(type)}/${enc(id)}/tags`, 'POST', pick);
export const removeRecordTag = (type: string, id: string, tagId: string) =>
  apiMutate<unknown>(`/records/${enc(type)}/${enc(id)}/tags/${enc(tagId)}/remove`, 'POST');
export const setFavorite = (type: string, id: string, on: boolean) =>
  apiMutate<{ isFavorite: boolean; changed: boolean }>(
    `/records/${enc(type)}/${enc(id)}/${on ? 'favorite' : 'unfavorite'}`,
    'POST',
  );
export const fetchRecordReminders = async (type: string, id: string) =>
  (await apiFetch<{ data: Reminder[] }>(`/records/${enc(type)}/${enc(id)}/reminders`)).data;
export const createReminder = (type: string, id: string, input: CreateReminderInput) =>
  apiMutate<Reminder>(`/records/${enc(type)}/${enc(id)}/reminders`, 'POST', input);
export const actOnReminder = (rid: string, action: 'complete' | 'dismiss' | 'cancel') =>
  apiMutate<Reminder>(`/reminders/${enc(rid)}/${action}`, 'POST');
export const snoozeReminder = (rid: string, input: SnoozeReminderInput) =>
  apiMutate<Reminder>(`/reminders/${enc(rid)}/snooze`, 'POST', input);
export function fetchMyReminders(
  status: string,
  limit: number,
  signal?: AbortSignal,
): Promise<{ data: Reminder[]; nextCursor: string | null }> {
  return apiFetch(`/reminders?status=${enc(status)}&limit=${limit}`, {
    ...(signal ? { signal } : {}),
  });
}
export const fetchAttachments = async (type: string, id: string) =>
  (await apiFetch<{ data: Attachment[] }>(`/records/${enc(type)}/${enc(id)}/attachments?limit=50`))
    .data;
export const initiateUpload = (
  type: string,
  id: string,
  input: {
    fileName: string;
    size: number;
    sha256: string;
    contentType?: string;
    clientToken: string;
  },
) => apiMutate<InitiateUploadResult>(`/records/${enc(type)}/${enc(id)}/attachments`, 'POST', input);
export function uploadContent(
  aid: string,
  body: Blob,
  onProgress: (fraction: number) => void,
): Promise<Attachment> {
  // XHR: yükleme ilerlemesi (fetch akış ilerlemesi tarayıcılarda tutarlı değil)
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `/api/attachments/${enc(aid)}/content`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('content-type', 'application/octet-stream');
    for (const [k, v] of Object.entries(csrfHeaders())) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(JSON.parse(xhr.responseText) as Attachment);
        return;
      }
      // Hata zarfı `ApiError`a çevrilir: tür reddinde sunucunun anlaşılır nedeni (M6-H5) kullanıcıya gösterilebilsin
      let parsed: ReturnType<typeof errorEnvelopeSchema.safeParse> | null = null;
      try {
        parsed = errorEnvelopeSchema.safeParse(JSON.parse(xhr.responseText));
      } catch {
        parsed = null;
      }
      if (parsed?.success) {
        const e = parsed.data.error;
        reject(new ApiError(e.code, e.message, e.requestId, xhr.status, e.details));
      } else reject(new Error(`HTTP ${xhr.status}`));
    };
    xhr.onerror = () => {
      reject(new Error('ağ hatası'));
    };
    xhr.send(body);
  });
}
export const finalizeUpload = (aid: string) =>
  apiMutate<Attachment>(`/attachments/${enc(aid)}/finalize`, 'POST');
/**
 * Başlatılmış yüklemenin kalan adımları. Aynı belirteçle yeniden başlatılan yükleme önceki eki döndürür: içerik zaten yüklendiyse
 * (`uploaded`) yeniden gönderilmez; ek tamamlandıysa (`active`) tamamlama da atlanır (sunucuda ikisi de zaten idempotent).
 */
export async function finishUpload(
  a: Pick<Attachment, 'id' | 'status'>,
  file: Blob,
  onProgress: (fraction: number) => void,
): Promise<void> {
  if (a.status === 'active') return;
  if (a.status !== 'uploaded') await uploadContent(a.id, file, onProgress);
  await finalizeUpload(a.id);
}
export const deleteAttachment = (aid: string) =>
  apiMutate<unknown>(`/attachments/${enc(aid)}/delete`, 'POST');

/**
 * İndirme: her seferinde YENİ kısa ömürlü belirteç alınır, adres hiçbir önbelleğe/duruma yazılmaz; tarayıcı bağlantıyı
 * izleyip indirir. Temiz olmayan dosya için bu fonksiyon çağrılmaz (arayüz `fileAvailability` ile kapatır).
 */
export async function downloadFile(aid: string): Promise<void> {
  const r = await apiMutate<{ url: string }>(`/attachments/${enc(aid)}/download-url`, 'POST');
  const a = document.createElement('a');
  a.href = `/api${r.url}`;
  a.rel = 'noopener';
  a.click();
}

// --- WP 2.9: Kişiler / Şirketler ---
export async function createCrmRecord(
  type: CrmRecordType,
  input: CreateCrmRecordInput,
  /** G8: niyet anahtarı (`lib/request-intent.ts`); ağ tekrarında aynı anahtar ikinci kayıt üretmez */
  requestKey?: string,
): Promise<RecordView> {
  return (
    await apiMutate<{ view: RecordView }>(
      `/records/${enc(type)}`,
      'POST',
      input,
      requestKey === undefined ? {} : { 'Idempotency-Key': requestKey },
    )
  ).view;
}
export async function updateCrmRecord(
  type: CrmRecordType,
  id: string,
  input: UpdateCrmRecordInput,
  version: string,
): Promise<RecordView> {
  const r = await apiFetch<{ view: RecordView }>(`/records/${enc(type)}/${enc(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json', ...csrfHeaders(), 'if-match': `"${version}"` },
    body: JSON.stringify(input),
  });
  return r.view;
}
export const fetchLookups = async () =>
  (await apiFetch<{ data: Lookup[] }>('/record-lookups')).data;
export const createLookup = (input: CreateLookupInput) =>
  apiMutate<Lookup>('/settings/lookups', 'POST', input);
export const updateLookup = (kind: LookupKind, key: string, input: UpdateLookupInput) =>
  apiMutate<Lookup>(`/settings/lookups/${enc(kind)}/${enc(key)}`, 'PATCH', input);
export const deleteLookup = (kind: LookupKind, key: string) =>
  apiMutate<null>(`/settings/lookups/${enc(kind)}/${enc(key)}/delete`, 'POST');
export const fetchSimilar = async (type: CrmRecordType, id: string) =>
  (await apiFetch<{ data: SimilarRecord[] }>(`/records/${enc(type)}/${enc(id)}/similar`)).data;
export async function checkSimilar(
  type: CrmRecordType,
  input: SimilarCheckInput,
  signal?: AbortSignal,
): Promise<SimilarRecord[]> {
  const r = await apiFetch<{ data: SimilarRecord[] }>(`/records/${enc(type)}/similar-check`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...csrfHeaders() },
    body: JSON.stringify(input),
    ...(signal ? { signal } : {}),
  });
  return r.data;
}
export const fetchMergePreview = (type: CrmRecordType, sourceId: string, targetId: string) =>
  apiFetch<MergePreview>(
    `/records/${enc(type)}/${enc(sourceId)}/merge-preview?targetId=${enc(targetId)}`,
  );
export const mergeRecords = (type: CrmRecordType, sourceId: string, targetId: string) =>
  apiMutate<unknown>(`/records/${enc(type)}/${enc(sourceId)}/merge`, 'POST', { targetId });
export const deleteRecord = (type: string, id: string) =>
  apiMutate<unknown>(`/records/${enc(type)}/${enc(id)}/delete`, 'POST');
export const fetchStats = (type: CrmRecordType, id: string) =>
  apiFetch<RecordStats>(`/records/${enc(type)}/${enc(id)}/stats`);
export function fetchCompanyContacts(
  id: string,
  cursor: string | null,
): Promise<CompanyContactsResponse> {
  const p = new URLSearchParams({ limit: '20' });
  if (cursor) p.set('cursor', cursor);
  return apiFetch(`/records/company/${enc(id)}/contacts?${p.toString()}`);
}
export function fetchActivity(
  type: string,
  id: string,
  cursor: string | null,
): Promise<ActivityResponse> {
  const p = new URLSearchParams({ limit: '20' });
  if (cursor) p.set('cursor', cursor);
  return apiFetch(`/records/${enc(type)}/${enc(id)}/activity?${p.toString()}`);
}
export const bulkRecordAction = (type: CrmRecordType, input: BulkRecordActionInput) =>
  apiMutate<BulkRecordActionResult>(`/records/${enc(type)}/bulk`, 'POST', input);
export const bulkTags = (
  type: string,
  input: { recordIds: string[]; add?: string[]; remove?: string[] },
) =>
  apiMutate<{ applied: string[]; rejected: Array<{ id: string; code: string }> }>(
    `/records/${enc(type)}/tags/bulk`,
    'POST',
    input,
  );
/** vCard/PDF: aynı kökenli GET (çerez); yanıt önbelleğe alınmaz (sunucu `no-store`). */
export function exportHref(type: string, id: string, kind: 'vcard' | 'pdf'): string {
  return `/api/records/${enc(type)}/${enc(id)}/${kind}`;
}

/** Sunucunun verdiği dosya adı (`Content-Disposition`); yoksa verilen yedek ad. */
export function fileNameFrom(disposition: string | null, fallback: string): string {
  const m = /filename="([^"]+)"/.exec(disposition ?? '');
  return m?.[1] ?? fallback;
}

/**
 * vCard/PDF indirme: yanıt önce denetlenir (hata gövdesi dosya olarak kaydedilmez), sonra geçici nesne adresiyle
 * indirilir; adres hemen serbest bırakılır.
 */
export async function downloadExport(
  type: string,
  id: string,
  kind: 'vcard' | 'pdf',
): Promise<void> {
  const res = await fetch(exportHref(type, id, kind), {
    credentials: 'same-origin',
    headers: { accept: kind === 'pdf' ? 'application/pdf' : 'text/vcard' },
  });
  if (!res.ok)
    throw new ApiError(
      res.status === 429 ? 'RATE_LIMITED' : 'INTERNAL',
      `HTTP ${res.status}`,
      res.headers.get('x-request-id') ?? '-',
      res.status,
    );
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileNameFrom(
      res.headers.get('content-disposition'),
      kind === 'pdf' ? 'kayit.pdf' : 'kayit.vcf',
    );
    a.rel = 'noopener';
    a.click();
  } finally {
    window.setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  }
}
export function fetchNotifications(
  cursor: string | null,
  signal?: AbortSignal,
): Promise<NotificationList> {
  const p = new URLSearchParams({ limit: '20' });
  if (cursor) p.set('cursor', cursor);
  return apiFetch(`/notifications?${p.toString()}`, { ...(signal ? { signal } : {}) });
}
export const markNotification = (id: string, action: 'read' | 'dismiss') =>
  apiMutate<unknown>(`/notifications/${enc(id)}/${action}`, 'POST');
export const markAllNotificationsRead = () => apiMutate<unknown>('/notifications/read-all', 'POST');

/** Dosyanın SHA-256 özeti (yükleme bütünlüğü; sunucu yeniden hesaplar). */
export async function sha256Hex(file: Blob): Promise<string> {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
